import { Position } from '../../domain/Position';
import { SideStatus } from '../../domain/Side';
import { SideReanimatedEvent } from '../../events/GameEvents';
/**
 * Reanimation trigger reasons
 */
export var ReanimationReason;
(function (ReanimationReason) {
    ReanimationReason["PLAYER_GIFT"] = "player_gift";
    ReanimationReason["TRAP_CHOICE"] = "trap_choice";
    ReanimationReason["TRAP_CAPTURE"] = "trap_capture";
    ReanimationReason["SPECIAL_EVENT"] = "special_event";
    ReanimationReason["ALLIANCE"] = "alliance";
})(ReanimationReason || (ReanimationReason = {}));
/**
 * ReanimationManager - Handles bringing eliminated sides back to the game
 *
 * According to design document:
 * - Eliminated sides can be brought back by giving them territory
 * - This can happen through trap resolution choices or player decisions
 * - Reanimated side gets the given territory as their starting position
 */
export class ReanimationManager {
    board;
    eventBus;
    constructor(board, eventBus) {
        this.board = board;
        this.eventBus = eventBus;
    }
    /**
     * Checks if a side can be reanimated
     */
    canReanimate(sideId) {
        const side = this.board.getSide(sideId);
        if (!side)
            return false;
        // Only eliminated sides can be reanimated — but NOT those that forfeited
        // (surrendered / timed out): those are permanently out, no comeback.
        return side.status === SideStatus.ELIMINATED && !side.isPermanentlyOut;
    }
    /**
     * Checks if a side can give territory for reanimation
     */
    canGiveTerritory(givingSide, territory) {
        const side = this.board.getSide(givingSide);
        if (!side || !side.isActive)
            return false;
        const territoryObj = this.board.getTerritoryAt(territory);
        if (!territoryObj)
            return false;
        // Must own the territory to give it
        if (territoryObj.owner !== givingSide)
            return false;
        // Can't give away last territory
        if (side.territories.size <= 1)
            return false;
        // Can't give away active field with piece on it
        if (territoryObj.occupiedBy !== null)
            return false;
        return true;
    }
    /**
     * Process reanimation request
     */
    processReanimation(request) {
        // Validate request
        if (!this.canReanimate(request.toSide)) {
            return {
                success: false,
                reanimatedSide: request.toSide,
                givenTerritory: request.territory,
                newStatus: SideStatus.ELIMINATED,
                message: 'Side cannot be reanimated',
            };
        }
        if (!this.canGiveTerritory(request.fromSide, request.territory)) {
            return {
                success: false,
                reanimatedSide: request.toSide,
                givenTerritory: request.territory,
                newStatus: SideStatus.ELIMINATED,
                message: 'Cannot give this territory',
            };
        }
        // Execute reanimation
        const eliminatedSide = this.board.getSide(request.toSide);
        const givingSide = this.board.getSide(request.fromSide);
        const territory = this.board.getTerritoryAt(request.territory);
        // Transfer territory ownership
        territory.capture(request.toSide);
        givingSide.loseTerritory(request.territory);
        // Territory is added by reanimate() method below
        // Reanimate the side
        eliminatedSide.reanimate(request.territory);
        // Place a piece on the territory for the reanimated side
        territory.occupy(request.toSide);
        // Emit event
        this.eventBus.publish(new SideReanimatedEvent(request.toSide, request.territory, request.fromSide, request.reason));
        return {
            success: true,
            reanimatedSide: request.toSide,
            givenTerritory: request.territory,
            newStatus: eliminatedSide.status,
            message: `Side ${request.toSide} reanimated at ${request.territory}`,
        };
    }
    /**
     * Handles reanimation from trap resolution
     */
    reanimateFromTrap(trappedSide, chosenRecipient, territory) {
        // Special case: if recipient is eliminated, reanimate them
        if (this.canReanimate(chosenRecipient)) {
            // The territory goes directly to eliminated side
            const eliminatedSide = this.board.getSide(chosenRecipient);
            const territoryObj = this.board.getTerritoryAt(territory);
            territoryObj.capture(chosenRecipient);
            // Territory is captured by capture() call above
            eliminatedSide.reanimate(territory);
            territoryObj.occupy(chosenRecipient);
            this.eventBus.publish(new SideReanimatedEvent(chosenRecipient, territory, trappedSide, ReanimationReason.TRAP_CHOICE));
            return {
                success: true,
                reanimatedSide: chosenRecipient,
                givenTerritory: territory,
                newStatus: eliminatedSide.status,
                message: `Side ${chosenRecipient} reanimated through trap choice`,
            };
        }
        // Otherwise, just transfer the territory normally
        const territoryObj = this.board.getTerritoryAt(territory);
        const recipientSide = this.board.getSide(chosenRecipient);
        territoryObj.capture(chosenRecipient);
        // Territory is already tracked via capture
        return {
            success: true,
            reanimatedSide: chosenRecipient,
            givenTerritory: territory,
            newStatus: recipientSide.status,
            message: `Territory transferred to Side ${chosenRecipient}`,
        };
    }
    /**
     * Gets all eliminated sides that could be reanimated
     */
    getEliminatedSides() {
        const eliminated = [];
        for (const [sideId, side] of this.board.sides) {
            if (side.status === SideStatus.ELIMINATED && !side.isPermanentlyOut) {
                eliminated.push(sideId);
            }
        }
        return eliminated;
    }
    /**
     * Gets territories that a side could give for reanimation
     */
    getGiveableTerritories(sideId) {
        const side = this.board.getSide(sideId);
        if (!side || !side.isActive)
            return [];
        const giveable = [];
        // Can't give away last territory
        if (side.territories.size <= 1)
            return [];
        for (const territoryKey of side.territories) {
            const position = Position.fromKey(territoryKey);
            const territory = this.board.getTerritoryAt(position);
            if (territory && territory.owner === sideId && territory.occupiedBy === null) {
                giveable.push(position);
            }
        }
        return giveable;
    }
    /**
     * Simulates reanimation for strategic planning
     */
    simulateReanimation(request) {
        // Create board clone for simulation
        const simulatedBoard = this.board.clone();
        const simulatedManager = new ReanimationManager(simulatedBoard, this.eventBus);
        return simulatedManager.processReanimation(request);
    }
    /**
     * Check if any side needs reanimation options after a turn
     */
    checkReanimationOpportunities() {
        const opportunities = new Map();
        // Check each active side
        for (const [sideId, side] of this.board.sides) {
            if (!side.isActive)
                continue;
            const giveable = this.getGiveableTerritories(sideId);
            if (giveable.length > 0) {
                opportunities.set(sideId, giveable);
            }
        }
        return opportunities;
    }
}
//# sourceMappingURL=ReanimationManager.js.map